import { useCallback, useEffect, useMemo, useState } from "react";
import { userReportsApi } from "../api";
import { dashboardsApi } from "../api/dashboards";
import { useAsync } from "./useAsync";
import type { UserDashboard, UserReport, UserWorkspace, ScheduledPlacement } from "../types/reports";

export type GridItem = { i: string; x: number; y: number; w: number; h: number };

export interface ScheduledDashboard {
  id: number;
  name: string;
}

/**
 * Owns all named-dashboard + workspace state and CRUD for the intelligence-home page.
 */
export function useDashboards() {
  const [dashboards, setDashboards] = useState<UserDashboard[]>([]);
  const [dashboardsLoading, setDashboardsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  // Workspaces
  const [workspaces, setWorkspaces] = useState<UserWorkspace[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(true);
  const [activeWorkspace, setActiveWorkspaceRaw] = useState<string>("__unassigned__");

  // Scheduled dashboards & placements
  const [scheduledDashboards, setScheduledDashboards] = useState<ScheduledDashboard[]>([]);
  const [placements, setPlacements] = useState<ScheduledPlacement[]>([]);

  // Load everything on mount in parallel
  useEffect(() => {
    Promise.all([
      userReportsApi.listDashboards().then((res) => {
        const list: UserDashboard[] = Array.isArray(res.data) ? res.data : [];
        setDashboards(list);
        return list;
      }),
      userReportsApi.listWorkspaces().then((res) => {
        const list: UserWorkspace[] = Array.isArray(res.data) ? res.data : [];
        setWorkspaces(list);
        return list;
      }),
      dashboardsApi.myList().then((res) => {
        const list: ScheduledDashboard[] = Array.isArray(res.data) ? res.data : [];
        setScheduledDashboards(list);
        return list;
      }).catch(() => { setScheduledDashboards([]); return []; }),
      userReportsApi.getScheduledPlacements().then((res) => {
        const list: ScheduledPlacement[] = Array.isArray(res.data) ? res.data : [];
        setPlacements(list);
        return list;
      }).catch(() => { setPlacements([]); return []; }),
    ]).then(([dashList, wsList]) => {
      // Determine initial workspace and tab
      if (wsList.length > 0) {
        const firstWs = wsList[0];
        setActiveWorkspaceRaw(firstWs.id);
        const firstInWs = (dashList as UserDashboard[]).find((d) => d.workspace_id === firstWs.id);
        if (firstInWs) setActiveTab(firstInWs.id);
      } else if ((dashList as UserDashboard[]).length > 0) {
        setActiveWorkspaceRaw("__unassigned__");
        setActiveTab((dashList as UserDashboard[])[0].id);
      }
    }).finally(() => {
      setDashboardsLoading(false);
      setWorkspacesLoading(false);
    });
  }, []);

  // When active workspace changes, reset active tab to first tab in that workspace
  const setActiveWorkspace = useCallback((wsId: string) => {
    setActiveWorkspaceRaw(wsId);
    if (wsId === "__scheduled__") {
      // Find first scheduled dashboard with no workspace placement (unassigned to workspace)
      const firstSched = scheduledDashboards.find((sd) => {
        const p = placements.find((pl) => pl.scheduled_dashboard_id === sd.id);
        return !p || !p.workspace_id;
      });
      setActiveTab(firstSched ? `sched-${firstSched.id}` : null);
    } else if (wsId === "__unassigned__") {
      const firstUnassigned = dashboards.find((d) => !d.workspace_id);
      setActiveTab(firstUnassigned?.id ?? null);
    } else {
      // Real workspace — find first user dashboard or scheduled dashboard in it
      const firstDash = dashboards.find((d) => d.workspace_id === wsId);
      if (firstDash) {
        setActiveTab(firstDash.id);
      } else {
        const firstSched = scheduledDashboards.find((sd) => {
          const p = placements.find((pl) => pl.scheduled_dashboard_id === sd.id);
          return p?.workspace_id === wsId;
        });
        setActiveTab(firstSched ? `sched-${firstSched.id}` : null);
      }
    }
  }, [dashboards, scheduledDashboards, placements]);

  // Compute tabs for a given workspace id
  const dashboardsForWorkspace = useCallback((wsId: string): Array<{ id: string; name: string; isScheduled?: boolean }> => {
    if (wsId === "__scheduled__") {
      return scheduledDashboards
        .filter((sd) => {
          const p = placements.find((pl) => pl.scheduled_dashboard_id === sd.id);
          return !p || !p.workspace_id;
        })
        .map((sd) => ({ id: `sched-${sd.id}`, name: sd.name, isScheduled: true }));
    }
    if (wsId === "__unassigned__") {
      return dashboards
        .filter((d) => !d.workspace_id)
        .map((d) => ({ id: d.id, name: d.name }));
    }
    const userDashes = dashboards
      .filter((d) => d.workspace_id === wsId)
      .map((d) => ({ id: d.id, name: d.name }));
    const schedDashes = scheduledDashboards
      .filter((sd) => {
        const p = placements.find((pl) => pl.scheduled_dashboard_id === sd.id);
        return p?.workspace_id === wsId;
      })
      .map((sd) => ({ id: `sched-${sd.id}`, name: sd.name, isScheduled: true }));
    return [...userDashes, ...schedDashes];
  }, [dashboards, scheduledDashboards, placements]);

  const activeTabIsScheduled = useMemo(() => {
    return activeTab?.startsWith("sched-") ?? false;
  }, [activeTab]);

  // Tiles for the active tab; re-runs on tab switch or refresh.
  const { data: tilesData, loading: tilesLoading } = useAsync<UserReport[]>(
    () =>
      activeTab && !activeTabIsScheduled
        ? userReportsApi.listPinned(activeTab).then((res) => (Array.isArray(res.data) ? res.data : []))
        : Promise.resolve([]),
    [activeTab, refreshKey, activeTabIsScheduled],
  );
  const tiles = tilesData ?? [];

  const saveTileLayouts = (items: GridItem[]) => {
    items.forEach((item) => {
      userReportsApi.updateLayout(item.i, { x: item.x, y: item.y, w: item.w, h: item.h });
    });
  };

  const refreshAll = () => setRefreshKey((k) => k + 1);

  // Drag-to-reorder tabs: optimistic reorder, persisted, reverted on failure.
  const reorder = async (orderedIds: string[]) => {
    const prev = dashboards;
    const byId = new Map(prev.map((d) => [d.id, d]));
    const reordered = orderedIds
      .map((id) => byId.get(id))
      .filter((d): d is UserDashboard => Boolean(d));
    setDashboards(reordered);
    try {
      await userReportsApi.reorderDashboards(orderedIds);
    } catch {
      setDashboards(prev);
    }
  };

  const handleDashboardCreated = (dashboard: UserDashboard) => {
    setDashboards((prev) => [...prev, dashboard]);
    setActiveTab(dashboard.id);
    setActiveWorkspaceRaw(dashboard.workspace_id ?? "__unassigned__");
  };

  const startRename = (dashboard: UserDashboard) => {
    setRenamingId(dashboard.id);
    setRenameValue(dashboard.name);
  };

  const cancelRename = () => setRenamingId(null);

  const commitRename = async (id: string) => {
    const name = renameValue.trim();
    setRenamingId(null);
    if (!name) return;
    const existing = dashboards.find((d) => d.id === id);
    if (existing && name === existing.name) return;
    try {
      const res = await userReportsApi.renameDashboard(id, name);
      setDashboards((prev) => prev.map((d) => (d.id === id ? { ...d, name: res.data.name } : d)));
    } catch {
      // silently revert
    }
  };

  const handleDeleteDashboard = async (id: string) => {
    if (!window.confirm("Delete this dashboard? Its tiles will be removed.")) return;
    setDeletingId(id);
    try {
      await userReportsApi.deleteDashboard(id);
      const remaining = dashboards.filter((d) => d.id !== id);
      setDashboards(remaining);
      if (activeTab === id) {
        const tabsInWs = dashboardsForWorkspace(activeWorkspace).filter((t) => t.id !== id);
        setActiveTab(tabsInWs.length > 0 ? tabsInWs[0].id : null);
      }
    } finally {
      setDeletingId(null);
    }
  };

  const activeLabel = useMemo(() => {
    if (activeTab?.startsWith("sched-")) {
      const schedId = parseInt(activeTab.replace("sched-", ""), 10);
      return scheduledDashboards.find((sd) => sd.id === schedId)?.name ?? "Scheduled Report";
    }
    return dashboards.find((d) => d.id === activeTab)?.name ?? "Dashboard";
  }, [dashboards, scheduledDashboards, activeTab]);

  // Workspace CRUD
  const createWorkspace = async (name: string) => {
    const res = await userReportsApi.createWorkspace(name);
    setWorkspaces((prev) => [...prev, res.data]);
    return res.data;
  };

  const renameWorkspace = async (id: string, name: string) => {
    const res = await userReportsApi.renameWorkspace(id, name);
    setWorkspaces((prev) => prev.map((ws) => (ws.id === id ? { ...ws, name: res.data.name } : ws)));
  };

  const deleteWorkspace = async (id: string) => {
    await userReportsApi.deleteWorkspace(id);
    setWorkspaces((prev) => prev.filter((ws) => ws.id !== id));
    // Unassign dashboards that were in this workspace
    setDashboards((prev) =>
      prev.map((d) => (d.workspace_id === id ? { ...d, workspace_id: null } : d))
    );
    if (activeWorkspace === id) {
      const remaining = workspaces.filter((ws) => ws.id !== id);
      if (remaining.length > 0) {
        setActiveWorkspace(remaining[0].id);
      } else {
        setActiveWorkspaceRaw("__unassigned__");
        setActiveTab(dashboards.find((d) => d.workspace_id !== id)?.id ?? null);
      }
    }
  };

  const reorderWorkspaces = async (ids: string[]) => {
    const prev = workspaces;
    const byId = new Map(prev.map((ws) => [ws.id, ws]));
    const reordered = ids.map((id) => byId.get(id)).filter((ws): ws is UserWorkspace => Boolean(ws));
    setWorkspaces(reordered);
    try {
      await userReportsApi.reorderWorkspaces(ids);
    } catch {
      setWorkspaces(prev);
    }
  };

  const promoteUnassigned = async (name: string) => {
    // Create a real workspace and move all currently unassigned dashboards into it
    const res = await userReportsApi.createWorkspace(name);
    const ws = res.data;
    setWorkspaces((prev) => [...prev, ws]);
    const unassigned = dashboards.filter((d) => !d.workspace_id);
    await Promise.all(
      unassigned.map((d) => userReportsApi.updateDashboard(d.id, { workspace_id: ws.id }))
    );
    setDashboards((prev) =>
      prev.map((d) => (!d.workspace_id ? { ...d, workspace_id: ws.id } : d))
    );
    setActiveWorkspaceRaw(ws.id);
  };

  const moveToWorkspace = async (tabId: string, isScheduled: boolean, targetWorkspaceId: string | null) => {
    if (isScheduled) {
      const schedId = parseInt(tabId.replace("sched-", ""), 10);
      const existing = placements.find((p) => p.scheduled_dashboard_id === schedId);
      const res = await userReportsApi.updateScheduledPlacement(schedId, {
        workspace_id: targetWorkspaceId,
        position: existing?.position ?? 0,
      });
      setPlacements((prev) => {
        const filtered = prev.filter((p) => p.scheduled_dashboard_id !== schedId);
        return [...filtered, res.data];
      });
    } else {
      const res = await userReportsApi.updateDashboard(tabId, { workspace_id: targetWorkspaceId });
      setDashboards((prev) => prev.map((d) => (d.id === tabId ? { ...d, workspace_id: res.data.workspace_id } : d)));
    }
  };

  return {
    // existing
    dashboards,
    tiles,
    tilesLoading,
    dashboardsLoading,
    activeTab,
    refreshKey,
    deletingId,
    renamingId,
    renameValue,
    activeLabel,
    setActiveTab,
    setRenameValue,
    refreshAll,
    reorder,
    saveTileLayouts,
    handleDashboardCreated,
    startRename,
    cancelRename,
    commitRename,
    handleDeleteDashboard,
    // new
    workspaces,
    workspacesLoading,
    activeWorkspace,
    setActiveWorkspace,
    scheduledDashboards,
    placements,
    activeTabIsScheduled,
    dashboardsForWorkspace,
    createWorkspace,
    renameWorkspace,
    deleteWorkspace,
    reorderWorkspaces,
    moveToWorkspace,
    promoteUnassigned,
  };
}
